import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { Table } from '~/eds/src/modules/Table/Table';
import { TableState } from '~/eds/src/types/Table';
import { BatchAddGroupNodeItem } from '~/redux/api/methods/group-nodes/types';

import { getResourceIcon } from '../helpers/getResourceIcon';
import { UseSearchForTypeResult } from '../hooks/useSearchForType';

type ResultRow = UseSearchForTypeResult & {
  resourceType: 'document' | 'ticket';
};

const messages = defineMessages({
  searchPrompt: {
    id: 'AddItemsResults.searchPrompt',
    defaultMessage: 'Search for documents or tickets to add to this group',
    description: 'Prompt message before searching',
  },
  noResults: {
    id: 'AddItemsResults.noResults',
    defaultMessage: 'No results found',
    description: 'Message when no search results are found',
  },
});

export interface AddItemsResultsProps {
  searchQuery: string;
  isLoading: boolean;
  documents: UseSearchForTypeResult[];
  tickets: UseSearchForTypeResult[];
  selectedRowIds: string[];
  onSelectionChange?: (items: BatchAddGroupNodeItem[]) => void;
}

const columns = [
  {
    key: 'name',
    title: 'Name',
    cellType: 'custom',
    width: 'auto',
    style: {
      borderRight: 'none',
    },
    renderCell: (row: ResultRow) => {
      return (
        <Layout align="center" spacing={2}>
          <Icon
            icon={getResourceIcon({
              resourceType: row.resourceType,
              filename: row.documentCurrentVersionName,
              fileType: row.fileType,
            })}
          />
          <Text>{row.name}</Text>
        </Layout>
      );
    },
  },
];

const tableOptions = {
  enableColumnHeaders: false,
  enableControls: false,
  enablePagination: false,
  enablePageSizeSelect: false,
  enableManageColumns: false,
  enableSelectRows: true,
};

export const AddItemsResults = ({
  searchQuery,
  isLoading,
  documents,
  tickets,
  selectedRowIds,
  onSelectionChange,
}: AddItemsResultsProps) => {
  const { formatMessage } = useIntl();

  const data = useMemo((): ResultRow[] => {
    const documentRows: ResultRow[] = documents.map((doc) => ({
      ...doc,
      resourceType: 'document',
    }));
    const ticketRows: ResultRow[] = tickets.map((ticket) => ({
      ...ticket,
      resourceType: 'ticket',
    }));
    return [...documentRows, ...ticketRows].sort((a, b) =>
      a.sort.localeCompare(b.sort),
    );
  }, [documents, tickets]);

  const handleUpdate = (updates: TableState) => {
    if (updates.selectedRowIds) {
      const selectedIdMap = updates.selectedRowIds.reduce<
        Record<string, boolean>
      >((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});

      onSelectionChange?.(
        data
          .filter((row) => selectedIdMap[row.id])
          .map((row) => ({
            resourceType: row.resourceType,
            resourceId: row.id,
          })),
      );
    }
  };

  if (!searchQuery) {
    return (
      <ContentContainer
        placeholderContent={{ message: formatMessage(messages.searchPrompt) }}
      />
    );
  }

  if (data.length === 0 && !isLoading) {
    return (
      <ContentContainer
        placeholderContent={{ message: formatMessage(messages.noResults) }}
      />
    );
  }

  return (
    <Table
      name="add-items-results"
      columns={columns}
      data={data}
      isLoading={isLoading}
      isBordered={false}
      hasContainerBorder={false}
      mode="select"
      options={tableOptions}
      state={{ selectedRowIds }}
      onUpdate={handleUpdate}
    />
  );
};
